import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Volume2, VolumeX } from "lucide-react";
import { useIsMobile, reveal } from "@/lib/motion";
import { GLASS, PHI, TYPE, GLOW, glassReveal } from "@/lib/glass";
import BunnyVideo from "@/components/living-light/BunnyVideo";
import { MediaLabel } from "@/components/evidence/MediaLabel";
import Eyebrow from "@/components/home/Eyebrow";
import RoomMarker from "@/components/home/RoomMarker";
import Seal from "@/components/home/Seal";

const SHELF_IMG =
  "https://static.prod-images.emergentagent.com/jobs/b9718452-4fce-415a-bc51-ea7625df7844/images/7d6d664502f9bd3e4011a0e9cadce4a84f6bc8e65369c05abd6a79fd886b3410.jpeg";

export const VIDEOS = {
  pieLid: "c9bda35b-30ac-406f-b8d3-3dd55e4f6a2d",
  lab: "972ab283-0578-4801-84c7-864b867889dd",
  labTwo: "da8ea290-ce6e-4f92-a4b6-165e8b11e484",
  liquidPixel: "34fce02b-dc97-4af2-a123-6d6ce3089e4c",
  manifesto: "d56798ba-0347-40c0-839f-b7d00975fece",
};

const caption = "text-center font-light text-[#F5F5F7]/85";
const captionStyle = { marginTop: PHI.s2, fontSize: TYPE.small, lineHeight: 1.65, letterSpacing: "0.03em", textShadow: GLOW.silverSoft };

export const IntelligentVideo = ({ videoUrl, portrait = false, ethereal = false }) => {
  const [audioAwoken, setAudioAwoken] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const iframeRef = useRef(null);
  const wrapperRef = useRef(null);
  const visibleRef = useRef(false);
  const instanceId = useId().replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
  const playerOrigin = new URL(videoUrl).origin;
  const syncAudio = (awake) => {
    const player = iframeRef.current?.contentWindow;
    if (!player) return;
    player.postMessage(JSON.stringify({ context: "player.js", method: awake ? "unmute" : "mute" }), playerOrigin);
    if (awake) player.postMessage(JSON.stringify({ context: "player.js", method: "setVolume", value: 1 }), playerOrigin);
  };
  const handleInteraction = () => {
    setInteracted(true);
    setAudioAwoken(true);
    const player = iframeRef.current?.contentWindow;
    if (player) {
      player.postMessage(JSON.stringify({ context: "player.js", method: "play" }), playerOrigin);
      player.postMessage(JSON.stringify({ context: "player.js", method: "unmute" }), playerOrigin);
      player.postMessage(JSON.stringify({ context: "player.js", method: "setVolume", value: 1 }), playerOrigin);
    }
  };
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting && entry.intersectionRatio >= 0.1;
      const player = iframeRef.current?.contentWindow;
      if (!player) return;
      if (visibleRef.current) {
        player.postMessage(JSON.stringify({ context: "player.js", method: "play" }), playerOrigin);
      } else {
        player.postMessage(JSON.stringify({ context: "player.js", method: "pause" }), playerOrigin);
      }
      const awake = audioAwoken && visibleRef.current;
      player.postMessage(JSON.stringify({ context: "player.js", method: awake ? "unmute" : "mute" }), playerOrigin);
      if (awake) player.postMessage(JSON.stringify({ context: "player.js", method: "setVolume", value: 1 }), playerOrigin);
    }, { threshold: 0.1 });
    
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [audioAwoken, playerOrigin]);
  return (
    <div
      ref={wrapperRef}
      data-testid={`intelligent-video-${instanceId}`}
      className={`relative mx-auto my-12 w-full overflow-hidden bg-[#030305] md:my-16 ${
        ethereal ? "intelligent-video--ethereal" : "border border-[#8E8E93]/15 shadow-[0_0_40px_rgba(132,223,244,0.05)]"
      } ${portrait ? (ethereal ? "aspect-[9/16] max-w-[21rem]" : "aspect-[9/16] max-w-[24rem]") : "aspect-video max-w-4xl"}`}
    >
      <div className={ethereal ? "intelligent-video-veil absolute inset-0" : "absolute inset-0"}>
        <iframe
          ref={iframeRef}
          data-testid={`intelligent-video-frame-${instanceId}`}
          title="Prism Pixel film"
          src={videoUrl}
          onLoad={() => {
            const player = iframeRef.current?.contentWindow;
            if (player && visibleRef.current) {
              player.postMessage(JSON.stringify({ context: "player.js", method: "play" }), playerOrigin);
            }
            syncAudio(audioAwoken && visibleRef.current);
          }}
          loading="lazy"
          style={{ border: 0, position: "absolute", top: 0, height: "100%", width: "100%" }}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
        />
      </div>
      {/* Founder's Overlay */}
      <div 
        data-testid={`intelligent-video-founder-overlay-${instanceId}`}
        onClick={handleInteraction}
        className={`absolute inset-0 z-20 flex cursor-pointer flex-col items-center justify-center bg-[#030305]/75 px-6 text-center backdrop-blur-md transition-all duration-1000 hover:bg-[#030305]/60 ${interacted ? "pointer-events-none opacity-0" : "opacity-100"}`}
      >
        <p data-testid={`intelligent-video-founder-quote-${instanceId}`} className="max-w-[28rem] font-light leading-relaxed text-[#F5F5F7] sm:text-lg" style={{ textShadow: GLOW.silverSoft }}>
          "I spent fifteen years in the quiet pursuit of this light.<br className="hidden sm:block" /> It is a profound honor to finally place it in your hands."
        </p>
        <p data-testid={`intelligent-video-founder-signature-${instanceId}`} className="mt-5 font-display text-sm font-light text-[#84DFF4]" style={{ letterSpacing: "0.05em" }}>
          — Charles Buerger
        </p>
        <div data-testid={`intelligent-video-experience-prompt-${instanceId}`} className="group mt-10 flex items-center gap-3 rounded-full border border-[#8E8E93]/40 bg-black/40 px-6 py-3 backdrop-blur-sm transition-colors hover:border-[#84DFF4] hover:bg-[#84DFF4]">
          <Play size={14} className="text-[#84DFF4] transition-colors group-hover:text-black" fill="currentColor" />
          <span className="font-mono-spec text-[0.6rem] uppercase tracking-[0.2em] text-[#8E8E93] transition-colors group-hover:text-black">Tap to experience</span>
        </div>
      </div>
      {ethereal && <div aria-hidden="true" className="intelligent-video-vignette" />}
      
      <button
        type="button"
        data-cursor
        data-testid={`intelligent-video-audio-${instanceId}`}
        aria-label={audioAwoken ? "Mute audio" : "Unmute audio"}
        aria-pressed={audioAwoken}
        onClick={(e) => {
          e.stopPropagation();
          const next = !audioAwoken;
          setAudioAwoken(next);
          syncAudio(next);
        }}
        className={`video-sound-toggle transition-opacity duration-700 ${interacted ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        {audioAwoken ? (
          <Volume2 size={18} className="text-[#84DFF4]" strokeWidth={1.5} aria-hidden="true" />
        ) : (
          <VolumeX size={18} className="text-[#84DFF4]" strokeWidth={1.5} aria-hidden="true" />
        )}
      </button>
    </div>
  );
};

const ARTIFACTS = [
  {
    testId: "artifacts-descriptor-glass",
    name: "PrismPixel Glass™",
    line: "Transparent projection substrate",
    spec: "15-micron sub-diffuse coating",
  },
  {
    testId: "artifacts-descriptor-opaque",
    name: "PrismPixel Glow™",
    line: "Opaque, high-luminance variant",
    spec: "For opaque projection surfaces",
  },
];

function MuseumPlate({ artifact, index, mobile }) {
  return (
    <motion.figure
      data-testid={artifact.testId}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      {...glassReveal(reveal(mobile, { duration: 0.7, delay: 0.1 + index * 0.12 }))}
      className="museum-plate relative m-0 text-center"
    >
      <figcaption
        className="font-display text-[#8E8E93]"
        style={{ fontSize: "0.72rem", letterSpacing: "0.26em", textTransform: "uppercase" }}
      >
        {artifact.name}
      </figcaption>
      <span
        aria-hidden="true"
        className="mx-auto block h-px w-8 bg-[#8E8E93]/35"
        style={{ marginTop: PHI.s2, marginBottom: PHI.s2, boxShadow: "0 0 12px rgba(132,223,244,0.25)" }}
      />
      <p
        className="font-light text-[#F5F5F7]"
        style={{ fontSize: TYPE.small, lineHeight: 1.6, letterSpacing: "0.02em", textShadow: GLOW.silverSoft }}
      >
        {artifact.line}
      </p>
      <p
        className="font-light uppercase text-[#F5F5F7]/50"
        style={{ fontSize: "0.68rem", marginTop: "0.55rem", letterSpacing: "0.14em" }}
      >
        {artifact.spec}
      </p>
    </motion.figure>
  );
}

function ActI({ mobile }) {
  return (
    <motion.div
      data-testid="act-artifacts"
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      {...glassReveal(reveal(mobile, { duration: 0.8 }))}
      className="mx-auto max-w-[760px]"
    >
      <div className="museum-frame">
        <div className="shelf-frame" style={{ boxShadow: GLOW.suspend }}>
          <img
            data-testid="artifacts-shelf"
            src={SHELF_IMG}
            alt="PrismPixel Glass and Glow laboratory bottles on a lit shelf"
            className="block w-full object-cover"
          />
          <div className="shelf-rim" aria-hidden="true">
            <span className="rim-l" />
            <span className="rim-r" />
          </div>
          <div className="shelf-sheen" aria-hidden="true" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6" style={{ marginTop: PHI.s3 }}>
        {ARTIFACTS.map((a, i) => (
          <MuseumPlate key={a.testId} artifact={a} index={i} mobile={mobile} />
        ))}
      </div>
    </motion.div>
  );
}

function LabFigure({ id, testId, videoId, index, children, delay = 200, style }) {
  return (
    <figure id={id} className="mx-auto max-w-[600px] scroll-mt-24" style={style}>
      <BunnyVideo testId={testId} videoId={videoId} loop soundToggle revealDelay={delay} />
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <MediaLabel kinds={["LAB_TEST", "RAW_VIDEO"]} testId={`${testId}-media-label`} />
        <span className="font-mono-spec text-[0.52rem] uppercase tracking-[0.2em] text-[#F5F5F7]/40">Fig. {String(index).padStart(2, "0")} · LT-{String(index).padStart(3, "0")}</span>
      </div>
      <figcaption className={caption} style={captionStyle}>{children}</figcaption>
    </figure>
  );
}

function ActII() {
  return (
    <div id="laboratory-evidence" data-testid="act-empirical" className="scroll-mt-24" style={{ marginTop: PHI.s5 }}>
      <Eyebrow testId="empirical-eyebrow" align="center" style={{ marginBottom: PHI.s3 }}>
        Laboratory Evidence
      </Eyebrow>
      <LabFigure id="video-pie-lid" testId="video-pie-lid" videoId={VIDEOS.pieLid} index={1} delay={0}>
        Arbitrary geometry mapping. Text remains sharp and legible across compound 3D curves.
      </LabFigure>
      <LabFigure id="video-lab" testId="video-lab" videoId={VIDEOS.lab} index={2} style={{ marginTop: PHI.s4 }}>
        Laboratory validation. The moment transparency met precision engineering.
      </LabFigure>
      <LabFigure id="video-lab-two" testId="video-lab-two" videoId={VIDEOS.labTwo} index={3} style={{ marginTop: PHI.s4 }}>
        PrismPixel Glow™ coated on transparent Mylar. An exceptionally perfect,
        extreme off-axis rear-projection surface — flexible, rollable, durable, and
        brilliant in all ambient-light environments.
      </LabFigure>
      <LabFigure id="video-liquid-pixel" testId="video-liquid-pixel" videoId={VIDEOS.liquidPixel} index={4} style={{ marginTop: PHI.s4 }}>
        The first success — 5½ years into the 15-year journey. Its formulation
        name is LiquidPixel™.
      </LabFigure>
    </div>
  );
}

function ActIII({ mobile }) {
  const videoRef = useRef(null);
  const [hot, setHot] = useState(false);

  return (
    <div data-testid="act-portals" style={{ marginTop: PHI.s5 }}>
      <Eyebrow testId="portals-eyebrow" align="center" style={{ marginBottom: PHI.s3 }}>
        The Engineering
      </Eyebrow>
      <motion.p
        data-testid="portals-bridge"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.9 }}
        {...glassReveal(reveal(mobile, { duration: 0.6 }))}
        style={{ fontSize: TYPE.body, lineHeight: 1.6, letterSpacing: "0.03em", textShadow: GLOW.silverSoft }}
        className="mx-auto max-w-[560px] text-center font-light text-[#F5F5F7]"
      >
        The engineering that turns glass into presence.
      </motion.p>

      <motion.button
        data-testid="portals-trigger"
        data-cursor
        type="button"
        onClick={() => videoRef.current?.enableSound()}
        onMouseEnter={() => setHot(true)}
        onMouseLeave={() => setHot(false)}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        {...glassReveal(reveal(mobile, { duration: 0.4 }))}
        style={{ marginTop: PHI.s3, marginBottom: PHI.s3 }}
        className="mx-auto flex min-h-[44px] flex-col items-center gap-4 bg-transparent text-center"
      >
        <span
          className={`font-light leading-[1.6] tracking-[0.02em] transition-colors duration-500 ${
            hot ? "text-white" : "text-[#F5F5F7]"
          }`}
          style={{ fontSize: "clamp(24px, 5vw, 28px)", textShadow: hot ? GLOW.silver : "none" }}
        >
          Beyond the Emissive Rectangle
        </span>
        <motion.span
          aria-hidden="true"
          animate={{ opacity: [0.35, 1, 0.35] }}
          transition={{ duration: hot ? 1.0 : 1.5, repeat: Infinity, ease: GLASS.refract }}
        >
          <Play size={32} className="text-[#84DFF4]" fill="#84DFF4" strokeWidth={0} />
        </motion.span>
      </motion.button>

      <div className="mx-auto w-full max-w-[900px]">
        <BunnyVideo
          ref={videoRef}
          testId="video-manifesto"
          videoId={VIDEOS.manifesto}
          playThreshold={0.3}
          fadeWithView
        />
      </div>
    </div>
  );
}

function ActIV({ mobile }) {
  return (
    <div data-testid="act-crucible" style={{ marginTop: PHI.s5 }}>
      <Seal variant="bold" testId="crucible-divider" />
      <Eyebrow testId="crucible-eyebrow" align="center" style={{ marginTop: PHI.s3 }}>
        The Crucible
      </Eyebrow>
      <motion.p
        data-testid="crucible-statement"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.9, delay: 0.1, ease: GLASS.settle }}
        style={{
          marginTop: PHI.s2,
          fontSize: "clamp(18px, 4vw, 22px)",
          letterSpacing: "0.05em",
          lineHeight: 1.8,
          textShadow: GLOW.silver,
        }}
        className="mx-auto max-w-[600px] text-center font-light text-[#F5F5F7]"
      >
        Fifteen years. 20,000 formulations. 19,700 failures. One answer.
      </motion.p>
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.9, delay: 0.5, ease: GLASS.settle }}
        style={{ marginTop: PHI.s3 }}
        className="text-center"
      >
        <Link
          to="/knowledge"
          data-testid="read-full-science"
          data-cursor
          className="inline-flex min-h-[44px] items-center bg-transparent text-xs font-light tracking-[0.28em] text-[#84DFF4]/70 transition-colors duration-500 hover:text-[#84DFF4]"
        >
          the full science →
        </Link>
      </motion.div>
    </div>
  );
}

export default function LivingLight() {
  const mobile = useIsMobile();
  return (
    <section
      data-testid="living-light"
      data-room="glass"
      className="room room--glass"
      style={{ paddingLeft: PHI.s3, paddingRight: PHI.s3, paddingTop: PHI.s6, paddingBottom: PHI.s5 }}
    >
      <div className="mx-auto w-full max-w-[62.5rem]">
        <RoomMarker
          numeral="II"
          label="Living Light · The Artifacts"
          align="center"
          ignite
          testId="living-light-eyebrow"
          style={{ marginBottom: PHI.s3 }}
        />
        <ActI mobile={mobile} />
        <ActII mobile={mobile} />
        <ActIII mobile={mobile} />
        <ActIV mobile={mobile} />
      </div>
    </section>
  );
}
